import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AdminSidebar } from '../../components/Sidebar';
import api from '../../utils/api';

const fmt = (iso) => iso ? new Date(iso).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('de-CH') : '—';

const KNOCKOUT_PHASES = ['Gruppenphase', 'Viertelfinale', 'Halbfinale', 'Finale'];
const MEDAL = { 1: '#F59E0B', 2: '#9CA3AF', 3: '#CD7F32' };

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ sessionId: '', phase: 'Runde' });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    api.get(`/admin/tournaments/${id}`).then(r => {
      setData(r.data);
      setEditName(r.data.tournament.name);
    });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addSession = async (e) => {
    e.preventDefault();
    if (!addForm.sessionId) return;
    setAdding(true);
    try {
      await api.post(`/admin/tournaments/${id}/sessions`, {
        sessionId: addForm.sessionId,
        phase: addForm.phase || 'Runde',
        roundNumber: (data?.rounds?.length || 0) + 1,
      });
      setShowAdd(false);
      setAddForm({ sessionId: '', phase: 'Runde' });
      load();
    } catch (e) {
      setMsg(e.response?.data?.error || 'Fehler');
    } finally { setAdding(false); }
  };

  const removeRound = async (tsId) => {
    if (!window.confirm('Runde aus dem Turnier entfernen?')) return;
    await api.delete(`/admin/tournaments/${id}/sessions/${tsId}`);
    load();
  };

  const saveName = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/tournaments/${id}`, { name: editName });
      setEditing(false);
      load();
    } finally { setSaving(false); }
  };

  const finishTournament = async () => {
    if (!window.confirm('Turnier abschliessen? Dies kann nicht ruckgangig gemacht werden.')) return;
    await api.patch(`/admin/tournaments/${id}`, { status: 'finished' });
    load();
  };

  const deleteTournament = async () => {
    if (!window.confirm('Turnier endgultig loschen?')) return;
    await api.delete(`/admin/tournaments/${id}`);
    navigate('/admin/tournaments');
  };

  if (!data) return <div style={{ display: 'flex' }}><AdminSidebar /><main className="main-content"><div className="spinner-wrap"><div className="spinner" /></div></main></div>;

  const { tournament, rounds, standings, availableSessions } = data;
  const isLiga = tournament.format === 'liga';
  const isFinished = tournament.status === 'finished';

  // Group knockout standings by phase
  const knockoutByPhase = !isLiga
    ? standings.reduce((acc, p) => {
        const ph = p.phase || 'Runde';
        if (!acc[ph]) acc[ph] = [];
        acc[ph].push(p);
        return acc;
      }, {})
    : null;

  return (
    <div style={{ display: 'flex' }}>
      <AdminSidebar />
      <main className="main-content">

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <Link to="/admin/tournaments" style={{ fontSize: 13, color: 'var(--text3)', textDecoration: 'none' }}>
            Zurück zu Turnieren
          </Link>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 8 }}>
            {editing ? (
              <div style={{ display: 'flex', gap: 8, flex: 1, alignItems: 'center' }}>
                <input className="form-input" value={editName} onChange={e => setEditName(e.target.value)}
                  style={{ fontSize: 22, fontWeight: 700, flex: 1 }} autoFocus />
                <button className="btn btn-primary btn-sm" onClick={saveName} disabled={saving}>Speichern</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Abbrechen</button>
              </div>
            ) : (
              <>
                <h1 style={{ fontSize: 26, flex: 1 }}>{tournament.name}</h1>
                {!isFinished && (
                  <button className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>Umbenennen</button>
                )}
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100,
              background: isLiga ? '#E8F0FE' : '#FBF5E6',
              color: isLiga ? '#1A56DB' : '#8B6A1C',
            }}>
              {isLiga ? 'Liga' : 'K.O.-System'}
            </span>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100,
              background: isFinished ? '#F3F4F6' : '#ECFDF5',
              color: isFinished ? '#6b7280' : '#059669',
            }}>
              {isFinished ? 'Abgeschlossen' : 'Aktiv'}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>{rounds.length} Runden</span>
          </div>
          {tournament.description && (
            <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>{tournament.description}</p>
          )}
        </div>

        {msg && <div className="alert alert-error" style={{ marginBottom: 16 }}>{msg}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

          {/* Left: Standings */}
          <div>
            {isLiga ? (
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                  Gesamtrangliste
                </div>
                {standings.length === 0 ? (
                  <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
                    Noch keine Runden hinzugefugt.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['#', 'Name', 'Runden', 'Richtig', 'Punkte'].map(h => (
                          <th key={h} style={{ padding: '6px 8px', fontSize: 11, fontWeight: 700, color: 'var(--text3)', textAlign: h === '#' || h === 'Punkte' ? 'center' : 'left', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((p) => (
                        <tr key={p.name_key} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 800, fontSize: 15, color: MEDAL[p.rank] || 'var(--text3)' }}>
                            {p.rank}
                          </td>
                          <td style={{ padding: '10px 8px', fontWeight: 600 }}>
                            {p.first_name} {p.last_name}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                            {p.rounds_played}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
                            {p.total_correct}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 800, color: 'var(--primary)', fontSize: 17 }}>
                            {p.total_score}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
              // Knockout: standings per phase
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {Object.keys(knockoutByPhase).length === 0 ? (
                  <div className="card" style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
                    Noch keine Runden hinzugefugt.
                  </div>
                ) : (
                  Object.entries(knockoutByPhase).map(([phase, players]) => (
                    <div key={phase} className="card">
                      <div style={{ fontWeight: 700, color: '#8B6A1C', marginBottom: 12, fontSize: 14 }}>
                        {phase}
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {players.map((p, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '8px', fontWeight: 700, color: MEDAL[p.rank_in_phase] || 'var(--text3)', textAlign: 'center', width: 28 }}>
                                {p.rank_in_phase}
                              </td>
                              <td style={{ padding: '8px', fontWeight: 600 }}>{p.first_name} {p.last_name}</td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: 'var(--primary)' }}>{p.total_score}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Right: Rounds + management */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Rounds list */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Runden ({rounds.length})
                </div>
                {!isFinished && (
                  <button className="btn btn-sm btn-primary" onClick={() => setShowAdd(p => !p)}>
                    {showAdd ? 'Abbrechen' : '+ Runde hinzufugen'}
                  </button>
                )}
              </div>

              {showAdd && (
                <form onSubmit={addSession} style={{ background: 'var(--bg)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>Spielsitzung wahlen</label>
                    <select className="form-select" value={addForm.sessionId}
                      onChange={e => setAddForm(p => ({ ...p, sessionId: e.target.value }))} required>
                      <option value="">— Sitzung wahlen —</option>
                      {availableSessions.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.title} · {fmtDate(s.started_at)} · {s.participant_count} Teiln.
                        </option>
                      ))}
                    </select>
                    {availableSessions.length === 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                        Keine abgeschlossenen Sitzungen verfugbar.
                      </div>
                    )}
                  </div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>
                      {isLiga ? 'Runden-Bezeichnung' : 'Phase'}
                    </label>
                    {isLiga ? (
                      <input className="form-input" placeholder="z.B. Nacht 1, Woche 3..."
                        value={addForm.phase} onChange={e => setAddForm(p => ({ ...p, phase: e.target.value }))} />
                    ) : (
                      <select className="form-select" value={addForm.phase}
                        onChange={e => setAddForm(p => ({ ...p, phase: e.target.value }))}>
                        {KNOCKOUT_PHASES.map(ph => <option key={ph} value={ph}>{ph}</option>)}
                      </select>
                    )}
                  </div>
                  <button className="btn btn-primary btn-sm" type="submit" disabled={adding || !addForm.sessionId}>
                    {adding ? 'Hinzufugen...' : 'Hinzufugen'}
                  </button>
                </form>
              )}

              {rounds.length === 0 ? (
                <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                  Noch keine Runden. Starten Sie einen Quiz und fugen Sie die Sitzung hier hinzu.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rounds.map((r) => (
                    <div key={r.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', background: 'var(--bg)', borderRadius: 8,
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'var(--primary)', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flexShrink: 0,
                      }}>
                        {r.round_number}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{r.phase}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                          {r.quiz_title || '—'} · {fmt(r.started_at)} · {r.participant_count} Teiln.
                        </div>
                      </div>
                      {!isFinished && (
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--error)', fontSize: 16, padding: '2px 6px' }}
                          onClick={() => removeRound(r.id)}
                          title="Entfernen"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            {!isFinished && (
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                  Aktionen
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Link to="/admin/quizzes" className="btn btn-secondary" style={{ textAlign: 'center' }}>
                    Neuen Quiz starten
                  </Link>
                  <button className="btn btn-ghost" style={{ color: '#059669', border: '1px solid #059669' }}
                    onClick={finishTournament}>
                    Turnier abschliessen
                  </button>
                  <button className="btn btn-ghost" style={{ color: 'var(--error)' }}
                    onClick={deleteTournament}>
                    Turnier loschen
                  </button>
                </div>
              </div>
            )}

            {isFinished && rounds.length > 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '20px 16px' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>&#127942;</div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Turnier abgeschlossen</div>
                {isLiga && standings[0] && (
                  <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                    Sieger: <strong>{standings[0].first_name} {standings[0].last_name}</strong> mit {standings[0].total_score} Punkten
                  </div>
                )}
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 12, color: 'var(--error)' }}
                  onClick={deleteTournament}>
                  Turnier loschen
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
